import { useNostr } from '@nostrify/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { DELETION_KIND } from '@/lib/nip25';
import { GOOD_WIKI_RELAYS_KIND, isValidWikiRelayList, relayListUrls } from '@/lib/nip51';

export interface PublisherRelay {
  /** Normalized relay URL (`wss://…/`). */
  url: string;
  /** Everyone whose kind 10102 list includes this relay — newest recommendation first. */
  addedBy: { pubkey: string; createdAt: number }[];
}

/**
 * The community "Publishers list": the union of every author's NIP-51
 * kind 10102 ("good wiki relays") list, deduped per author to their
 * newest list (it's a replaceable kind) and honoring NIP-09 deletions of
 * whole lists. Sorted by number of recommenders, then URL.
 */
export function usePublisherRelays() {
  const { nostr } = useNostr();

  return useQuery<PublisherRelay[]>({
    queryKey: ['publishers', 'relays'],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(8000)]);
      const events = await nostr.query([{ kinds: [GOOD_WIKI_RELAYS_KIND], limit: 500 }], { signal });
      const valid = events.filter(isValidWikiRelayList);
      if (valid.length === 0) return [];

      // Latest list per author (relays should already enforce this for a
      // replaceable kind — belt and braces for relays that don't).
      const byAuthor = new Map<string, NostrEvent>();
      for (const event of valid) {
        const existing = byAuthor.get(event.pubkey);
        if (!existing || event.created_at > existing.created_at) byAuthor.set(event.pubkey, event);
      }
      const lists = [...byAuthor.values()];

      // Honor NIP-09 deletions retracting an entire list. Only a deletion
      // authored by the same pubkey as the list counts.
      const deletions = await nostr.query(
        [{ kinds: [DELETION_KIND], '#e': lists.map((l) => l.id), limit: lists.length + 50 }],
        { signal },
      );
      const active = lists.filter(
        (l) =>
          !deletions.some(
            (d) => d.pubkey === l.pubkey && d.tags.some(([n, v]) => n === 'e' && v === l.id),
          ),
      );

      const byUrl = new Map<string, PublisherRelay>();
      for (const list of active) {
        for (const url of relayListUrls(list)) {
          const entry = byUrl.get(url) ?? { url, addedBy: [] };
          entry.addedBy.push({ pubkey: list.pubkey, createdAt: list.created_at });
          byUrl.set(url, entry);
        }
      }
      const relays = [...byUrl.values()];
      for (const relay of relays) relay.addedBy.sort((a, b) => b.createdAt - a.createdAt);
      relays.sort((a, b) => b.addedBy.length - a.addedBy.length || a.url.localeCompare(b.url));
      return relays;
    },
    staleTime: 30_000,
  });
}

/**
 * Publishes the current user's whole kind 10102 relay list (it's one
 * replaceable event, so add and remove both work by writing the full new
 * set). Callers compute the new set from `usePublisherRelays` data —
 * keeping this hook free of a second fetch of the user's own list.
 */
export function useUpdatePublisherList() {
  const { user } = useCurrentUser();
  const { mutateAsync: publishEvent } = useNostrPublish();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (urls: string[]) => {
      if (!user) throw new Error('Log in to edit your publisher list.');
      return publishEvent({
        kind: GOOD_WIKI_RELAYS_KIND,
        content: '',
        tags: [
          ...urls.map((url) => ['relay', url]),
          ['alt', 'Wiki publisher relay recommendations (NIP-51 kind 10102, "good wiki relays")'],
        ],
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['publishers'] });
    },
  });
}
